// BUILD-046 correction cycle 1bb88b3e (stage B1): the state of a list that is read PAGE BY PAGE from the server - the completed tasks of Team Work
// (`GET /work/oversight?status=completed`) and "Requests I issued" (`GET /work/issued`). listStateModel.ts models a list that arrives in one answer; these
// two arrive in pages with an opaque `next` cursor, so they need three more facts: how many are loaded, whether more exist, and whether the server serves
// the route at all.
//
// Phases (exactly one is shown):
//   idle         : nothing requested yet (a Caller change returns here; a view asks again when it is shown). Not an empty list.
//   loading      : the first page is being read. No items, no empty text, no error text.
//   ready        : the first page arrived. `items` may be empty (the empty text) and `next` says whether more exist.
//   unavailable  : the server does not serve this route (an older server). Not an empty list: the view says so in words.
//   error        : any other failure. Not an empty list: the view says what failed and offers Retry.
// Loading more never replaces what is loaded: a failure keeps the loaded items and reports `moreError`. An answer is applied only if it is for the
// LATEST first-page request of the latest Caller epoch, so the previous Caller's items can never appear after a Caller change (Req 2.7).
//
// `complete` (ready and no `next`) is what a client-side search needs to be truthful: while more pages exist, a search over the loaded items is a search
// over the LOADED items, and the view must say so (viewSearchModel.ts).
//
// Pure module: no React, no I/O. Runtime import: the list-state helpers. Erasable TypeScript only.

import { toListError } from './listStateModel';
import type { ListError } from './listStateModel';

export type PagedPhase = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

export interface PagedState<T> {
  phase: PagedPhase;
  /** The Caller epoch of the latest first-page request, then its number within the epoch. */
  epoch: number;
  seq: number;
  items: readonly T[];
  /** The cursor of the next page; null when this is the last page (or nothing is loaded). */
  next: string | null;
  loadingMore: boolean;
  /** Why the last "load more" failed; the loaded items are kept. */
  moreError: string | null;
  /** Non-null in `error` and `unavailable`. */
  error: ListError | null;
}

export interface PageAnswer<T> {
  items: readonly T[];
  next: string | null;
}

export function initialPaged<T>(epoch = 0): PagedState<T> {
  return { phase: 'idle', epoch, seq: 0, items: [], next: null, loadingMore: false, moreError: null, error: null };
}

// ───────────────────────── first page ─────────────────────────

/** Starts a first-page read (mount, Retry, reload after an action, a changed filter). Supersedes everything before it: nothing stale shows while loading. */
export function pagedRequest<T>(state: PagedState<T>, epoch: number): { state: PagedState<T>; seq: number } {
  const seq = state.epoch === epoch ? state.seq + 1 : 1;
  return { state: { phase: 'loading', epoch, seq, items: [], next: null, loadingMore: false, moreError: null, error: null }, seq };
}

function isCurrent<T>(state: PagedState<T>, epoch: number, seq: number): boolean {
  return state.epoch === epoch && state.seq === seq;
}

function validNext(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The first page arrived. An answer that is not a list is a failure, never an empty list. */
export function pagedSuccess<T>(state: PagedState<T>, epoch: number, seq: number, page: PageAnswer<T> | null | undefined): PagedState<T> {
  if (state.phase !== 'loading' || !isCurrent(state, epoch, seq)) return state;
  if (page === null || page === undefined || !Array.isArray(page.items)) {
    return { ...state, phase: 'error', items: [], next: null, error: { kind: 'invalid-response', status: 200, code: null, message: 'The server answered, but not in the expected form.' } };
  }
  return { ...state, phase: 'ready', items: page.items, next: validNext(page.next), loadingMore: false, moreError: null, error: null };
}

/** The first page failed. `unavailable` is true when the server does not serve the route (the caller decides: see isRouteUnavailable). */
export function pagedFailure<T>(state: PagedState<T>, epoch: number, seq: number, thrown: unknown, unavailable: boolean): PagedState<T> {
  if (state.phase !== 'loading' || !isCurrent(state, epoch, seq)) return state;
  return { ...state, phase: unavailable ? 'unavailable' : 'error', items: [], next: null, loadingMore: false, moreError: null, error: toListError(thrown) };
}

// ───────────────────────── more pages ─────────────────────────

/** The "Load more" click. `send` is true exactly when the next page must now be requested; a second click while it runs sends nothing. */
export function pagedMoreRequest<T>(state: PagedState<T>): { state: PagedState<T>; send: boolean; cursor: string | null } {
  if (state.phase !== 'ready' || state.next === null || state.loadingMore) return { state, send: false, cursor: null };
  return { state: { ...state, loadingMore: true, moreError: null }, send: true, cursor: state.next };
}

/** The next page arrived: appended after what is loaded, without repeating an item the loaded pages already hold (paging by cursor can overlap). */
export function pagedMoreSuccess<T>(state: PagedState<T>, epoch: number, seq: number, page: PageAnswer<T> | null | undefined, keyOf: (item: T) => string): PagedState<T> {
  if (state.phase !== 'ready' || !state.loadingMore || !isCurrent(state, epoch, seq)) return state;
  if (page === null || page === undefined || !Array.isArray(page.items)) {
    return { ...state, loadingMore: false, moreError: 'The server answered, but not in the expected form.' };
  }
  const known = new Set(state.items.map(keyOf));
  const added = page.items.filter((item) => {
    const key = keyOf(item);
    if (known.has(key)) return false;
    known.add(key);
    return true;
  });
  return { ...state, items: [...state.items, ...added], next: validNext(page.next), loadingMore: false, moreError: null };
}

export function pagedMoreFailure<T>(state: PagedState<T>, epoch: number, seq: number, thrown: unknown): PagedState<T> {
  if (state.phase !== 'ready' || !state.loadingMore || !isCurrent(state, epoch, seq)) return state;
  const message = toListError(thrown).message;
  return { ...state, loadingMore: false, moreError: message };
}

// ───────────────────────── small edits ─────────────────────────

/** Replaces the loaded item with this key by `update(item)` (an entry re-read, an accepted change). Anything not loaded is left alone. */
export function pagedPatch<T>(state: PagedState<T>, key: string, keyOf: (item: T) => string, update: (item: T) => T): PagedState<T> {
  if (state.phase !== 'ready') return state;
  let changed = false;
  const items = state.items.map((item) => {
    if (keyOf(item) !== key) return item;
    changed = true;
    return update(item);
  });
  return changed ? { ...state, items } : state;
}

// ───────────────────────── what the view shows ─────────────────────────

export interface PagedViewText {
  label: string;
  emptyText: string;
  unavailableText: string;
}

export type PagedView<T> =
  | { kind: 'idle' }
  | { kind: 'loading'; text: string }
  | { kind: 'unavailable'; text: string }
  | { kind: 'error'; heading: string; detail: string; canRetry: true }
  | { kind: 'empty'; text: string }
  | { kind: 'data'; items: readonly T[]; loadedCount: number; complete: boolean; canLoadMore: boolean; loadingMore: boolean; moreError: string | null };

export function describePaged<T>(state: PagedState<T>, text: PagedViewText): PagedView<T> {
  switch (state.phase) {
    case 'idle':
      return { kind: 'idle' };
    case 'loading':
      return { kind: 'loading', text: `Loading ${text.label}…` };
    case 'unavailable':
      return { kind: 'unavailable', text: text.unavailableText };
    case 'error':
      return { kind: 'error', heading: `Could not load ${text.label}`, detail: state.error?.message ?? 'Something went wrong.', canRetry: true };
    case 'ready':
      if (state.items.length === 0 && state.next === null) return { kind: 'empty', text: text.emptyText };
      return {
        kind: 'data',
        items: state.items,
        loadedCount: state.items.length,
        // Every page is loaded only when the server named no next cursor.
        complete: state.next === null,
        canLoadMore: state.next !== null && !state.loadingMore,
        loadingMore: state.loadingMore,
        moreError: state.moreError,
      };
  }
}

/** The ids a "ready" list holds, for the accepted-action notices (actionOutcomeMemory.presentIdsOf); null while nothing can be said about what it holds. */
export function pagedPresentIds<T>(state: PagedState<T>, idOf: (item: T) => string): string[] | null {
  return state.phase === 'ready' ? state.items.map(idOf) : null;
}
